<template>
    <div class="menu-button-wrapper">
        <Button
            id="settings-menu"
            type="button"
            icon="pi pi-bars"
            severity="secondary"
            @click="toggle"
            aria-haspopup="true"
            aria-controls="overlay_menu"
        />
        <span v-if="hasUnreleasedChanges" class="menu-dot" />
    </div>
    <Menu ref="settingsMenu" :model="menuItems" :popup="true">
        <template #item="{ item, props }">
            <a v-ripple class="flex items-center" v-bind="props.action">
                <span v-if="item.icon" :class="item.icon" />
                <img v-if="item.img" :src="item.img" class="profile-img" />
                <span>{{ item.label }}</span>
                <span v-if="item.dot" class="item-dot" />
                <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
                <span
                    v-if="item.shortcut"
                    class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1"
                >
                    {{ item.shortcut }}
                </span>
            </a>
        </template>
    </Menu>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useKeybindingsExplanation } from "@/composables/keybindings-explanation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useReleaseManager, useReleaseStore } from "@/stores/release-store";
import { useSteksManager } from "@/stores/steks-store";
import { useUIStore } from "@/stores/ui-store";
import { useUserManager } from "@/stores/user-store";
import { useWhatsNewManager, useWhatsNewStore } from "@/stores/whats-new-store";

const ui = useUIStore();
const keybindings = useKeybindingsExplanation();
const { signOut } = useAuthenticationStore();
const { authStatus } = useAuthenticationManager();
const isSignedIn = computed(() => authStatus.value === AuthStatus.SIGNED_IN);
const { photoUrl, currentOrganizationPermissions, user, isPlatformAdmin } =
    useUserManager();

const { unreleasedChanges, initialized: releasesInitialized } =
    useReleaseManager();
const { listReleases, checkUnreleasedChanges } = useReleaseStore();
const hasUnreleasedChanges = computed(() => Boolean(unreleasedChanges.value));

const { hasUnseen: hasUnseenWhatsNew, unseenCount: unseenWhatsNewCount } =
    useWhatsNewManager();

const whatsNewStore = useWhatsNewStore();
// Dialog requires Vue's inject() context; initialize during component setup().
whatsNewStore.initDialog();

const settingsMenu = ref();
const toggle = (event: Event) => {
    settingsMenu.value.toggle(event);
};
const router = useRouter();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();

// Check for unreleased changes when stek becomes available
watch(
    () => stek.value?.identity,
    async (stekId) => {
        if (stekId) {
            await listReleases();
        }
    },
    { immediate: true }
);

// Re-check on route navigation, but only when we don't already know about
// unreleased changes (once detected, the dot stays until a release clears it)
const route = useRoute();
watch(
    () => route.fullPath,
    () => {
        if (releasesInitialized.value && !unreleasedChanges.value) {
            checkUnreleasedChanges();
        }
    }
);
const { organizations } = useOrganizationsManager();
const { steks } = useSteksManager();

const canManageUsers = computed(() => {
    return currentOrganizationPermissions.value?.can_manage_users ?? false;
});

const canManageSteks = computed(() => {
    return currentOrganizationPermissions.value?.can_manage_steks ?? false;
});

const canManageBilling = computed(() => {
    return currentOrganizationPermissions.value?.can_manage_billing ?? false;
});

const canAccessOrgSettings = computed(() => {
    return (
        canManageSteks.value || canManageUsers.value || canManageBilling.value
    );
});

const canManageStekSettings = computed(() => {
    return (
        currentOrganizationPermissions.value?.can_manage_stek_settings ?? false
    );
});

const canCreateStek = computed(() => {
    return currentOrganizationPermissions.value?.can_create_stek ?? false;
});

const hasOrganization = computed(() => {
    // Checking for name since that's used for the menu/navigation.
    return Boolean(organization.value?.name);
});

const hasMultipleOrganizations = computed(() => {
    return Boolean(organizations.value && organizations.value.length > 1);
});

const hasStek = computed(() => {
    // Checking for name since that's used for the menu/navigation.
    return Boolean(stek.value?.name);
});

const hasMultipleSteks = computed(() => {
    return Boolean(steks.value && steks.value.length > 1);
});

const canViewActivityMetrics = computed(() => {
    // Check if user has permission to view activity metrics in ANY organization
    if (!user.value?.permissions) return false;
    return Object.values(user.value.permissions).some(
        (perms) => perms.can_view_activity_metrics
    );
});

const menuItems = computed(() => [
    {
        label: "Settings",
        icon: photoUrl.value ? "" : "pi pi-user",
        img: photoUrl.value,
        class: "setting profile",
        command: () => router.push({ name: "settings" }),
        visible: isSignedIn.value,
    },
    {
        label: ui.darkMode ? "Light mode" : "Dark Mode",
        icon: ui.darkMode ? "pi pi-sun" : "pi pi-moon",
        command: () => ui.toggleDarkMode(),
    },
    {
        separator: true,
        visible: canAccessOrgSettings.value,
    },
    {
        label: "Organization Settings",
        icon: "pi pi-building",
        command: () =>
            router.push({
                name: "organizationSettings",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: canAccessOrgSettings.value,
    },
    {
        label: organization.value
            ? "Switch Organization"
            : "Select Organization",
        icon: "pi pi-sync",
        command: () => router.push({ name: "organizationPage" }),
        visible: hasMultipleOrganizations.value,
    },
    {
        separator: true,
        visible:
            (hasStek.value && canManageStekSettings.value) ||
            (hasOrganization.value && canCreateStek.value) ||
            (hasOrganization.value && hasMultipleSteks.value),
    },
    {
        label: "Stek Settings",
        icon: "pi pi-cog",
        dot: hasUnreleasedChanges.value,
        command: () => router.push({ name: "stekSettings" }),
        visible: hasStek.value && canManageStekSettings.value,
    },
    {
        label: "Create new stek",
        icon: "pi pi-plus-circle",
        command: () =>
            router.push({
                name: "createStek",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: hasOrganization.value && canCreateStek.value,
    },
    {
        label: stek.value ? "Switch Stek" : "Select Stek",
        icon: "pi pi-sync",
        command: () =>
            router.push({
                name: "organizationHome",
                params: {
                    orgName: organization.value?.name,
                },
            }),
        visible: hasOrganization.value && hasMultipleSteks.value,
    },
    { separator: true },
    {
        label: "Docs",
        icon: "pi pi-book",
        command: () => router.push({ name: "docsHome" }),
    },
    {
        label: "What's New",
        icon: "pi pi-gift",
        badge: hasUnseenWhatsNew.value ? unseenWhatsNewCount.value : undefined,
        command: () => whatsNewStore.openModal(true),
    },
    {
        separator: true,
    },
    {
        label: "Help",
        icon: "pi pi-info-circle",
        command: () => router.push({ name: "help" }),
        class: "setting help",
    },
    {
        label: "Keybindings",
        icon: "pi pi-question-circle",
        command: () => keybindings.open(),
    },
    { separator: true },
    {
        label: "Logout",
        icon: "pi pi-sign-out",
        command: () => signOut(),
        visible: isSignedIn.value,
    },
    {
        separator: true,
        visible: canViewActivityMetrics.value || isPlatformAdmin.value,
    },
    {
        label: "Platform Admin",
        icon: "pi pi-shield",
        command: () => router.push({ name: "platformAdmin" }),
        // Platform-wide flag — org-scoped permissions deliberately don't
        // surface this entry (backend admin endpoints 403 without it).
        visible: isPlatformAdmin.value,
    },
    {
        label: "Activity Metrics",
        icon: "pi pi-chart-line",
        command: () =>
            router.push({
                name: "activityMetrics",
            }),
        visible: canViewActivityMetrics.value,
    },
]);
</script>

<style lang="scss">
.profile-img {
    width: var(--p-icon-size);
    height: var(--p-icon-size);
    border-radius: 50%;
    object-fit: cover;
}

.menu-button-wrapper {
    position: relative;
    display: inline-flex;
}

.menu-dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    background-color: var(--p-red-500);
    border-radius: 50%;
    pointer-events: none;
}

.item-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: var(--p-red-500);
    border-radius: 50%;
    margin-left: 0.5rem;
}
</style>
